import { AssetAmount } from "@sundaeswap/asset";
import type { IComposedTx, ITxBuilderSubmit } from "@sundaeswap/core";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import { Tx, TxComplete } from "lucid-cardano";
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ADA_METADATA } from "../../../constants/cardano.constants";
import { useAdaBalance } from "../../../hooks/useAdaBalance";
import { ETransactionBuilderState } from "../../../stores/ExchangeContext";
import { IEnhancedMergedPosition, IEnhancedValue } from "../../../types/Farm.types";
import { ETransactionState } from "../../../types/Transaction.types";
import { bigintToString } from "../../../utils/string-format";

interface IYieldFarmingPositionDialogContextProps extends PropsWithChildren {
  mergedPosition?: IEnhancedMergedPosition;
}

export interface IYieldFarmingPositionDialogContext {
  builtTx?: IComposedTx<Tx, TxComplete>;
  dialogState: ETransactionState;
  insufficientAdaBalance: boolean;
  onAssetSelect: (assetId: string, notLockedAssetsWithBalanceFromValue: IEnhancedValue[]) => void;
  onUpdatePositionsValue: (assetId: string, value: string) => void;
  onRemovePositionValue: (assetIdToRemove: string) => void;
  positionValues?: IEnhancedMergedPosition;
  setPositionValues: Dispatch<SetStateAction<IEnhancedMergedPosition>>;
  resetContext: () => void;
  setDialogState: Dispatch<SetStateAction<ETransactionState>>;
  setTransactionBuilderState: Dispatch<
    SetStateAction<ETransactionBuilderState>
  > /** TODO: use from order context? */;
  showAssetSelect?: boolean;
  toggleAssetSelect: () => void;
  setBuiltTx: Dispatch<
    SetStateAction<IComposedTx<Tx, TxComplete>>
  > /** TODO: use from order context? */;
  setTx: Dispatch<SetStateAction<ITxBuilderSubmit>> /** TODO: use from order context? */;
  tx?: ITxBuilderSubmit /** TODO: use from order context? */;
  transactionBuilderState?: ETransactionBuilderState /** TODO: use from order context? */;
}

const defaultYieldFarmingPositionState: IYieldFarmingPositionDialogContext = {
  builtTx: undefined,
  dialogState: ETransactionState.initial,
  insufficientAdaBalance: false,
  onAssetSelect: () => {},
  onUpdatePositionsValue: () => {},
  onRemovePositionValue: () => {},
  positionValues: undefined,
  setPositionValues: () => {},
  resetContext: () => {},
  setDialogState: () => {},
  setTransactionBuilderState: () => {},
  setBuiltTx: () => {},
  setTx: () => {},
  showAssetSelect: undefined,
  toggleAssetSelect: () => {},
  tx: undefined,
  transactionBuilderState: undefined,
};

const YieldFarmingPositionDialogContext = createContext(defaultYieldFarmingPositionState);

export const useYieldFarmingPositionDialogContext = () =>
  useContext(YieldFarmingPositionDialogContext);

/**
 * For ADA, there's a minimum value of 5 ADA that need to be locked up to cover minUTXO and to cover the Cardano Transaction Fees if a user wants to withdraw.
 */
export const MIN_ADA_LIMIT = new AssetAmount(5000000, ADA_METADATA);

export const YieldFarmingPositionDialogContextProvider: FC<
  IYieldFarmingPositionDialogContextProps
> = ({ children, mergedPosition }) => {
  const [positionValues, setPositionValues] = useState<IEnhancedMergedPosition>({
    value: [],
    delegation: [],
    inputs: [],
  });
  const { adaBalanceFromWallet: ada } = useAdaBalance();
  const [dialogState, setDialogState] = useState(ETransactionState.initial);
  const [transactionBuilderState, setTransactionBuilderState] =
    useState<ETransactionBuilderState>();
  const [builtTx, setBuiltTx] = useState<IComposedTx<Tx, TxComplete>>();
  const [tx, setTx] = useState<ITxBuilderSubmit>();
  const [showAssetSelect, setShowAssetSelect] = useState(false);

  const adaBalance = useMemo(() => ada?.quantity, [ada?.quantity?.amount]);
  const insufficientAdaBalance = useMemo(() => {
    let stakedAdaBalance =
      positionValues?.value
        ?.filter(({ asset }) => SundaeUtils.isAdaAsset(asset.metadata))
        ?.reduce((total, { amount }) => total + BigInt(amount), 0n) ?? 0n;

    if (stakedAdaBalance > MIN_ADA_LIMIT.amount) {
      return false;
    }

    if (adaBalance?.amount) {
      return adaBalance?.amount + stakedAdaBalance < MIN_ADA_LIMIT.amount;
    }

    return true;
  }, [adaBalance, positionValues]);

  /**
   * Effect to control the initial state for `positionValues`.
   */
  useEffect(() => {
    if (
      // If positionValues exist and the `Value` fields isn't empty, we update the state with the actual `mergedPosition` value
      positionValues &&
      positionValues.value.length
      /*!Object.keys(positionValues).every(
        (key) => (positionValues[key as keyof IEnhancedMergedPosition] ?? []).length === 0
      )*/
    ) {
      return;
    }

    if (mergedPosition) setPositionValues(mergedPosition);
  }, [mergedPosition, positionValues]);

  /**
   * Handles adding a new asset from the `notLockedAssetsFromBalance` values to the state
   */
  const handleAddNewAsset = useCallback(
    (assetId: string, notLockedAssetsWithBalanceFromValue: IEnhancedValue[]) => {
      setPositionValues((prevValues) => {
        const matchingAsset = notLockedAssetsWithBalanceFromValue.find(
          (item) => item.asset.metadata.assetId === assetId
        );
        if (matchingAsset) {
          return {
            ...prevValues,
            value: [...prevValues.value, matchingAsset],
          };
        }
        return prevValues;
      });
    },
    []
  );

  /**
   * Handles changing the _lockedAmount_ of a single item in the state.
   */
  const handleUpdatePositionsValue = useCallback((assetId: string, value: string) => {
    setPositionValues((prevValues) => {
      return {
        ...prevValues,
        value: prevValues.value.map((item) => {
          const isMatch = item.asset.metadata.assetId === assetId;
          if (isMatch) {
            const newAssetAmount = AssetAmount.fromValue(value, item.asset.metadata);
            return {
              ...item,
              amount: bigintToString(newAssetAmount.amount), // TODO: lovelaces?
              assetAmount: newAssetAmount,
            } as IEnhancedValue;
          } else {
            return item;
          }
        }),
      };
    });
  }, []);

  const handleRemovePositionValue = useCallback((assetIdToRemove: string) => {
    setPositionValues((prevValues) => ({
      ...prevValues,
      value: prevValues?.value?.filter(({ assetID }) => assetID !== assetIdToRemove),
    }));
  }, []);

  const toggleAssetSelect = useCallback(() => setShowAssetSelect((prev) => !prev), []);
  const handleAssetSelect = useCallback(
    (assetId: string, notLockedAssetsWithBalanceFromValue: IEnhancedValue[]) => {
      handleAddNewAsset(assetId, notLockedAssetsWithBalanceFromValue);
      toggleAssetSelect();
    },
    []
  );

  /** Resets the positionValues in the context state */
  const resetContext = useCallback(() => {
    if (mergedPosition) setPositionValues(mergedPosition);
    setDialogState(ETransactionState.initial);
  }, [mergedPosition]);

  return (
    <YieldFarmingPositionDialogContext.Provider
      value={{
        builtTx,
        dialogState,
        setDialogState,
        insufficientAdaBalance,
        onAssetSelect: handleAssetSelect,
        onUpdatePositionsValue: handleUpdatePositionsValue,
        onRemovePositionValue: handleRemovePositionValue,
        positionValues,
        setPositionValues,
        resetContext,
        showAssetSelect,
        setTransactionBuilderState,
        setBuiltTx,
        setTx,
        toggleAssetSelect,
        transactionBuilderState,
        tx,
      }}
    >
      {children}
    </YieldFarmingPositionDialogContext.Provider>
  );
};
